import { Avatar, Box, LinearProgress } from "@mui/material";
import {
	DataGridPro,
	GRID_CHECKBOX_SELECTION_COL_DEF,
	gridClasses,
} from "@mui/x-data-grid-pro";
import { useEffect, useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import RefreshIcon from "@mui/icons-material/Refresh";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import { daysBetween } from "utils/Times";
import { Context } from "./Context";
import { Filter } from "components/filter/Filter";
import { getAvatar } from "utils/Styles";
import { RenderCell } from "./RenderCell";
import { ContextMenu } from "components/table/schedule/ContextMenu";
import { AddShift } from "components/dialog/schedule/AddShift";
import { EditShift } from "components/dialog/schedule/EditShift";
import { DeleteShift } from "components/dialog/schedule/DeleteShift";
import { DialogModal } from "components/dialog/paycode/DialogModel";
import { TimekeeperService } from "services/TimekeeperService";
import { Form as PayCodeForm } from "components/dialog/paycode/Form";
import { useSnackbar } from "notistack";
import moment from "moment";

const Table = () => {
	const { t } = useTranslation();

	const timekeeperService = new TimekeeperService();

	const {
		tableDateRange,
		rows,
		openAddShift,
		setOpenAddShift,
		openEditShift,
		setOpenEditShift,
		openDeleteShift,
		setOpenDeleteShift,
		employees,
		checked,
		setChecked,
		shiftId,
		setShiftId,
		routeAddShiftDay,
		setRouteAddShiftDay,
		loadingGettingSchedule,
		reloading,
	} = useContext(Context);

	const [paycodes, setPaycodes] = useState([]);

	const { enqueueSnackbar } = useSnackbar();
	const [contextMenu, setContextMenu] = useState(null);
	const [cell, setCell] = useState({ column: null, row: null });

	useEffect(() => {
		if (!openAddShift) {
			return;
		}

		const defaultEmployeeId =
			checked.length > 0 ? checked[0] : employees[0]?.id;
		const defaultDay =
			routeAddShiftDay ||
			(tableDateRange[0] && tableDateRange[0].isValid()
				? tableDateRange[0].format("YYYY-MM-DD")
				: null);

		if (defaultEmployeeId && checked.length === 0) {
			setChecked([defaultEmployeeId]);
		}

		if (defaultDay || defaultEmployeeId) {
			setCell((prev) => ({
				...prev,
				column: defaultDay || prev.column,
				row: defaultEmployeeId ? `${defaultEmployeeId}` : prev.row,
			}));
		}
	}, [
		checked,
		employees,
		openAddShift,
		routeAddShiftDay,
		setChecked,
		tableDateRange,
	]);

	useEffect(() => {
		const fetchData = async () => {
			if (paycodes.length < 1) {
				const allPaycodes = await timekeeperService.paycodes.getPaycodes();
				if (allPaycodes) {
					setPaycodes(allPaycodes);
				}
			}
		};
		fetchData();
		// eslint-disable-next-line
	}, [contextMenu, paycodes]);

	const columns = useMemo(
		() => [
			{
				field: "avatar",
				headerName: t("timekeeper.schedule.table.columns.avatar"),
				align: "center",
				width: 65,
				resizable: false,
				renderCell: (params) => {
					return (
						<Box sx={{ pt: 1, pb: 1 }}>
							<Avatar
								sx={{
									bgcolor: getAvatar(params.row.full_name),
								}}
							>
								{params.row.full_name.charAt(0)}
							</Avatar>
						</Box>
					);
				},
			},
			{
				field: "full_name",
				headerName: t("timekeeper.schedule.table.columns.full_name"),
				flex: 2,
				minWidth: 300,
			},
			{
				field: "document_employee",
				headerName: t(
					"timekeeper.schedule.table.columns.document_number"
				),
				align: "center",
				flex: 2,
				minWidth: 200,
			},
			...daysBetween(tableDateRange[0], tableDateRange[1]).map((day) => ({
				field: day,
				headerName: day,
				headerAlign: "center",
				flex: 1,
				minWidth: 180,
				renderCell: (params) => (
					<RenderCell params={params} setCell={setCell} />
				),
			})),
			{
				field: "planned_hours",
				headerName: "Planned Hours",
				align: "center",
				width: 100,
				resizable: false,
			},
		],
		[t, tableDateRange]
	);

	const handlePayCodeSubmission = async (paycode, isUpdate = false) => {
		enqueueSnackbar("Guardando...", {
			variant: "info",
			autoHideDuration: 1000,
		});

		try {
			if (isUpdate) {
				await timekeeperService.paycodes.updatePaycodeAssignEmployee(
					paycode.id,
					paycode
				);
			} else {
				await timekeeperService.paycodes.savePaycodeAssignEmployee(paycode);
			}
			enqueueSnackbar("Código de pago guardado exitosamente", {
				variant: "success",
				autoHideDuration: 3000,
			});
			reloading();
		} catch (error) {
			const axiosError = error;
			const errorMessage =
				axiosError.response?.data?.exception ?? "Unknown error";

			enqueueSnackbar(
				`Error al guardar el código de pago. ${errorMessage}`,
				{
					variant: "formAlert",
					autoHideDuration: null,
					anchorOrigin: {
						vertical: "bottom",
						horizontal: "left",
					},
					Form: DialogModal,
					dataForm: {
						openModal: isUpdate
							? openUpdatePayCodeModal
							: openAddPayCodeModal,
						handleCloseModal: isUpdate
							? handleCloseUpdatePayCode
							: handleCloseAddPayCode,
						handleSubmitForm: isUpdate
							? handleSubmitUpdatePayCode
							: handleSubmitAddPayCode,
						title: isUpdate
							? "Editar código de pago"
							: "Agregar código de pago",
						FormComponent: PayCodeForm,
						item: paycode,
						maxWidth: "md",
						items: paycodes,
						setOpen: isUpdate
							? setOpenUpdatePayCodeModal
							: setOpenAddPayCodeModal,
					},
				}
			);
		}
	};

	const handleSubmitAddPayCode = (paycode) =>
		handlePayCodeSubmission(paycode);
	const handleSubmitUpdatePayCode = (paycode) =>
		handlePayCodeSubmission(paycode, true);

	const [openAddPayCodeModal, setOpenAddPayCodeModal] = useState(false);
	const handleCloseAddPayCode = () => setOpenAddPayCodeModal(false);

	const [openUpdatePayCodeModal, setOpenUpdatePayCodeModal] = useState(false);
	const handleCloseUpdatePayCode = () => setOpenUpdatePayCodeModal(false);

	const options = useMemo(
		() => [
			{
				privilege: true,
				name: t("timekeeper.schedule.table.menu.add_shift"),
				click: () => {
					setContextMenu(null);
					setRouteAddShiftDay(
						moment(cell?.column, "YYYY-MM-DD").isValid()
							? moment(cell?.column, "YYYY-MM-DD").format(
									"YYYY-MM-DD"
							  )
							: tableDateRange[0]?.format("YYYY-MM-DD") || null
					);
					setOpenAddShift(true);
				},
				icon: <WorkHistoryIcon />,
				divider: false,
				disabled: !JSON.parse(
					localStorage.getItem("permissions")
				)?.find((permision) => permision === "scheduler.CreateShift"),
				seleted: false,
				onlyOne: false,
				dialog: (
					<AddShift
						open={openAddShift}
						setOpen={setOpenAddShift}
						cell={cell}
						setCell={setCell}
						checked={checked}
						employees={employees}
						reloading={reloading}
					/>
				),
				permission: JSON.parse(
					localStorage.getItem("permissions")
				)?.find((permision) => permision === "scheduler.CreateShift")
					? true
					: false,
			},
			{
				privilege: true,
				name: t("timekeeper.schedule.table.menu.reload"),
				click: () => {
					setContextMenu(null);
					reloading();
				},
				icon: <RefreshIcon />,
				divider: true,
				disabled: false,
				seleted: false,
				onlyOne: false,
				dialog: undefined,
				permission: true,
			},
		],
		[
			checked,
			cell,
			employees,
			openAddShift,
			reloading,
			setOpenAddShift,
			setRouteAddShiftDay,
			t,
			tableDateRange,
		]
	);

	return (
		<Box
			sx={{
				[`.${gridClasses.cell}.open`]: {
					backgroundColor: (theme) =>
						theme.palette.background.default,
				},
				[`.${gridClasses.cell}.close`]: {
					backgroundColor: (theme) => theme.palette.background.close,
				},
			}}
		>
			<div
				onContextMenu={(event) => {
					event.preventDefault();

					setContextMenu({
						mouseX: event?.clientX + 2,
						mouseY: event?.clientY - 6,
					});
				}}
			>
				<DataGridPro
					disableVirtualization
					slots={{
						toolbar: useCallback(
							() => <Filter view={"schedule"} />,
							[]
						),
						loadingOverlay: LinearProgress,
					}}
					slotProps={{
						cell: {
							onContextMenu: (e) => {
								if (e?.target?.dataset?.field) {
									setCell({
										column: e?.target?.dataset?.field,
										row: e?.currentTarget?.getAttribute(
											"data-id"
										),
									});
								} else {
									setCell({
										column: e?.currentTarget?.getAttribute(
											"data-field"
										),
										row: e?.currentTarget?.parentElement.getAttribute(
											"data-id"
										),
									});
								}
							},
						},
					}}
					pinnedColumns={{
						left: [
							GRID_CHECKBOX_SELECTION_COL_DEF.field,
							"avatar",
							"full_name",
							"document_employee",
						],
					}}
					columns={columns}
					rows={rows.filter((row) =>
						moment(
							row?.contract_end_date,
							"YYYY-MM-DDTHH:mm:ss"
						).isAfter(tableDateRange[0])
					)}
					disableColumnMenu
					showCellVerticalBorder
					showColumnVerticalBorder
					disableColumnResize
					hideFooter
					sx={{
						border: (theme) => `1px solid ${theme.palette.divider}`,
						height: "calc(100vh - 120px)",
					}}
					checkboxSelection
					disableRowSelectionOnClick
					getRowHeight={(params) => {
						const { model } = params;

						let length = 64;

						for (const key in model) {
							let counter = 0;

							if (model[key].shifts) {
								counter =
									counter + model[key].shifts.length * 47;
							}

							if (model[key].hours) {
								counter =
									counter + model[key].hours.length * 47;
							}

							if (model[key].days) {
								counter = counter + model[key].days.length * 47;
							}

							if (counter > length) {
								length = counter;
							}
						}

						return length;
					}}
					getCellClassName={(params) => {
						if (!moment(params?.field, "YYYY-MM-DD").isValid()) {
							return "";
						}

						const style = moment(
							params?.row?.pay_period_end,
							"YYYY-MM-DD"
						).isSameOrAfter(moment(params?.field, "YYYY-MM-DD"));

						return style ? "close" : "open";
					}}
					rowSelectionModel={checked}
					onRowSelectionModelChange={(newRowSelectionModel) => {
						setChecked(newRowSelectionModel);
					}}
					loading={loadingGettingSchedule}
				/>

				<ContextMenu
					options={options}
					contextMenu={contextMenu}
					setContextMenu={setContextMenu}
					checked={checked}
				/>

				<EditShift
					shiftId={shiftId}
					setShiftId={setShiftId}
					open={openEditShift}
					setOpen={setOpenEditShift}
					employees={employees}
					reloading={reloading}
				/>

				<DeleteShift
					shiftId={shiftId}
					setShiftId={setShiftId}
					open={openDeleteShift}
					setOpen={setOpenDeleteShift}
					reloading={reloading}
				/>
			</div>
		</Box>
	);
};

export { Table };
